import { forwardRef, HTMLAttributes, useState, useMemo } from 'react';
import { m, AnimatePresence, Variants } from 'framer-motion';
import { clsx } from 'clsx';
import { PortfolioGridContext } from '../PortfolioCard/PortfolioCard';

// Omit HTML animation events that conflict with Framer Motion
type MotionSafeHTMLAttributes<T> = Omit<
  HTMLAttributes<T>,
  'onAnimationStart' | 'onAnimationEnd' | 'onAnimationIteration' | 'onDrag' | 'onDragStart' | 'onDragEnd'
>;

export type GridColumns = 1 | 2 | 3 | 4;

export interface PortfolioGridProps extends MotionSafeHTMLAttributes<HTMLDivElement> {
  columns?: GridColumns;
  gap?: 'sm' | 'md' | 'lg';
  staggerChildren?: boolean;
}

const columnStyles: Record<GridColumns, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 md:grid-cols-2',
  3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
  4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
};

const gapStyles: Record<string, string> = {
  sm: 'gap-4',
  md: 'gap-6',
  lg: 'gap-8',
};

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: 'easeOut',
    },
  },
};

export const PortfolioGrid = forwardRef<HTMLDivElement, PortfolioGridProps>(
  (
    {
      columns = 3,
      gap = 'md',
      staggerChildren = true,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const [hoveredId, setHoveredId] = useState<string | null>(null);

    const contextValue = useMemo(
      () => ({
        isAnyHovered: hoveredId !== null,
        hoveredId,
      }),
      [hoveredId]
    );

    return (
      <PortfolioGridContext.Provider value={contextValue}>
        <m.div
          ref={ref}
          variants={staggerChildren ? containerVariants : undefined}
          initial={staggerChildren ? 'hidden' : undefined}
          animate={staggerChildren ? 'visible' : undefined}
          className={clsx(
            'grid w-full',
            columnStyles[columns],
            gapStyles[gap],
            className
          )}
          {...props}
        >
          <AnimatePresence mode="popLayout">
            {Array.isArray(children)
              ? children.map((child, index) => {
                  if (!child || typeof child !== 'object') return child;

                  const childId = (child as any).props?.id || `item-${index}`;

                  return (
                    <m.div
                      key={childId}
                      variants={staggerChildren ? itemVariants : undefined}
                      layout
                      onMouseEnter={() => setHoveredId(childId)}
                      onMouseLeave={() => setHoveredId(null)}
                    >
                      {child}
                    </m.div>
                  );
                })
              : children}
          </AnimatePresence>
        </m.div>
      </PortfolioGridContext.Provider>
    );
  }
);

PortfolioGrid.displayName = 'PortfolioGrid';
